/**
 * Reading a `.zip` (docs/PARITY-STEP-4-PLAN.md section 10): an icon pack. Entries are found
 * from the central directory (packs written with data descriptors have no sizes up front),
 * stored or deflated ones are read (the browser's own `deflate-raw`), and every limit is
 * checked as it's read, not trusted from the file: how many entries, each one's size after
 * inflating, and the whole - so a small zip can't expand into gigabytes. Folders inside it
 * are flattened to file names, and nothing is ever written to disk.
 */

export interface ZipFile {
  /** Its file name, without the folders it's in. */
  readonly name: string;
  /** Where it is in the zip, folders and all: for showing or checking, never for writing to disk. */
  readonly path: string;
  /** Null when it couldn't be read, with why in `problem`. */
  readonly data: Uint8Array | null;
  readonly problem?: string;
}

export interface UnzipLimits {
  readonly entries: number;
  /** Past this, an entry is reported as too big rather than read. */
  readonly entryBytes: number;
  readonly totalBytes: number;
}

export const ICON_PACK_LIMITS: UnzipLimits = { entries: 2_000, entryBytes: 4 * 1024 * 1024, totalBytes: 64 * 1024 * 1024 };

export class ZipError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipError';
  }
}

/** Inflates at most `cap` + 1 bytes: one more than allowed is enough to know it's too big. */
async function inflate(compressed: Uint8Array, cap: number): Promise<Uint8Array> {
  const reader = new Blob([compressed as Uint8Array<ArrayBuffer>]).stream().pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      total += value.byteLength;
      if (total > cap) break;
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  const out = new Uint8Array(Math.min(total, cap + 1));
  let at = 0;
  for (const chunk of chunks) {
    const part = chunk.subarray(0, Math.min(chunk.byteLength, out.byteLength - at));
    out.set(part, at);
    at += part.byteLength;
    if (at >= out.byteLength) break;
  }
  return out;
}

export async function unzip(bytes: Uint8Array, limits: UnzipLimits = ICON_PACK_LIMITS): Promise<ZipFile[]> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const u16 = (at: number) => view.getUint16(at, true);
  const u32 = (at: number) => view.getUint32(at, true);
  const fits = (at: number, length: number) => at >= 0 && length >= 0 && at + length <= bytes.byteLength;
  // The end-of-directory record is in the last 64 KB and 22 bytes (after a comment).
  let end = -1;
  for (let at = bytes.byteLength - 22; at >= Math.max(0, bytes.byteLength - 22 - 65_535); at--) {
    if (u32(at) === 0x06054b50) {
      end = at;
      break;
    }
  }
  if (end < 0) throw new ZipError("That isn't a zip file.");
  const count = u16(end + 10);
  const directory = u32(end + 16);
  if (count > limits.entries) throw new ZipError(`It has more than ${limits.entries.toLocaleString('en')} files.`);
  const decoder = new TextDecoder();
  const files: ZipFile[] = [];
  let total = 0;
  let at = directory;
  for (let n = 0; n < count; n++) {
    if (!fits(at, 46) || u32(at) !== 0x02014b50) throw new ZipError("The zip's list of files is damaged.");
    const flags = u16(at + 8);
    const method = u16(at + 10);
    const compressedSize = u32(at + 20);
    const size = u32(at + 24);
    const nameLength = u16(at + 28);
    const next = at + 46 + nameLength + u16(at + 30) + u16(at + 32);
    const local = u32(at + 42);
    if (!fits(at + 46, nameLength)) throw new ZipError("The zip's list of files is damaged.");
    const path = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength));
    at = next;
    // Folders, and macOS's resource forks, aren't icons.
    if (path.endsWith('/') || path.includes('__MACOSX')) continue;
    const name = path.slice(path.lastIndexOf('/') + 1);
    if (flags & 1) {
      files.push({ name, path, data: null, problem: "it's encrypted." });
      continue;
    }
    if (compressedSize === 0xffffffff || size === 0xffffffff || local === 0xffffffff) {
      files.push({ name, path, data: null, problem: "it's too big for a pack." });
      continue;
    }
    if (size > limits.entryBytes) {
      files.push({ name, path, data: null, problem: 'over the size limit.' });
      continue;
    }
    if (!fits(local, 30) || u32(local) !== 0x04034b50) throw new ZipError('The zip is damaged.');
    const start = local + 30 + u16(local + 26) + u16(local + 28);
    if (!fits(start, compressedSize)) throw new ZipError('The zip is damaged.');
    const raw = bytes.subarray(start, start + compressedSize);
    let data: Uint8Array;
    if (method === 0) data = raw.slice(0, Math.min(raw.byteLength, limits.entryBytes + 1));
    else if (method === 8) {
      try {
        data = await inflate(raw, limits.entryBytes);
      } catch {
        files.push({ name, path, data: null, problem: "it couldn't be unpacked." });
        continue;
      }
    } else {
      files.push({ name, path, data: null, problem: "it's packed in a way Realmery can't read." });
      continue;
    }
    if (data.byteLength > limits.entryBytes) {
      files.push({ name, path, data: null, problem: 'over the size limit.' });
      continue;
    }
    total += data.byteLength;
    if (total > limits.totalBytes) throw new ZipError('It unpacks to more than a pack can hold.');
    files.push({ name, path, data });
  }
  return files;
}
